import {
  DEFAULT_EXPIRATION_DAYS,
  removeCookie,
  setCookie,
} from '@aboutyou/utils';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import type {StoreApi, UseBoundStore} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';
import {createSharedStore} from '../sharedStore';
import {getIsConsentBannerAccepted} from '../utils/consentBannerAcceptedState';
import type {StoreWithSubscribeSelector} from './types';

export type CookieProviderProps = {
  serverCookies?: {
    [key: string]: string | undefined;
  };
  browserCookies?: {
    [key: string]: string | undefined;
  };
  defaultCookieDomain?: string;
};
interface RawValueStoreState {
  store: Record<string, string | undefined>;
  update: (slice: Record<string, string | undefined>) => void;
}
export interface CookieStoreState {
  store: Record<string, unknown>;
  update: (slice: Record<string, unknown>) => void;
}

type CookieContextValue = {
  useRawValueStore: UseBoundStore<StoreApi<RawValueStoreState>>;
  useTransformedValueStore: StoreWithSubscribeSelector;
  updateCookie: (options: {
    cookieName: string;
    nextValue: unknown;
    config?: {domain?: string; path?: string; expirationInDays?: number};
    forcePersistValue_ILLEGALLY?: boolean;
  }) => void;
};

const CookieContext = createContext<CookieContextValue>(
  new Proxy<CookieContextValue>({} as CookieContextValue, {
    get() {
      throw new Error(
        'Cannot find value for CookieContext. Did you add a CookieProvider in one of the parent components?',
      );
    },
  }),
);

export const useTransformedValueStore = () => {
  const useStoreBase = useContext(CookieContext).useTransformedValueStore;
  return useStoreBase;
};

export const useRawValueStore = () => {
  const useStoreBase = useContext(CookieContext).useRawValueStore;
  return useStoreBase;
};

export const useRawCookieValue__PRIVATE = (cookieName: string) => {
  const rawValue = useContext(CookieContext).useRawValueStore(
    state => state.store[cookieName],
  );

  return rawValue;
};

export const useStoredValue = (cookieName: string) => {
  const storeValue = useContext(CookieContext).useTransformedValueStore(
    state => state.store[cookieName],
  );

  return storeValue;
};

export const useUpdateValue = () => {
  return useContext(CookieContext).updateCookie;
};

export const EMPTY_VALUE = Symbol('EMPTY_VALUE');

export function CookieProvider({
  serverCookies,
  browserCookies,
  defaultCookieDomain,
  children,
}: PropsWithChildren<CookieProviderProps>) {
  const useRawValueStore = useRef(
    createSharedStore<RawValueStoreState>(set => ({
      store: serverCookies ?? {},
      update: (cookies: Record<string, string | undefined>) => {
        return set(state => ({store: {...state.store, ...cookies}}));
      },
    })),
  ).current;

  useEffect(() => {
    if (browserCookies && getIsConsentBannerAccepted()) {
      useRawValueStore.getState().update(browserCookies);
    }
  }, [browserCookies, useRawValueStore]);

  const useTransformedValueStore = useRef(
    createSharedStore(
      subscribeWithSelector<CookieStoreState>(set => ({
        store: {},
        update: (slice: Record<string, unknown>) => {
          return set(state => ({store: {...state.store, ...slice}}));
        },
      })),
    ),
  ).current;

  const updateCookie: CookieContextValue['updateCookie'] = useCallback(
    ({cookieName, nextValue, config, forcePersistValue_ILLEGALLY}) => {
      if (nextValue === undefined) {
        removeCookie({name: cookieName});
        useTransformedValueStore.getState().update({[cookieName]: EMPTY_VALUE});
        return;
      }

      if (getIsConsentBannerAccepted() || forcePersistValue_ILLEGALLY) {
        setCookie({
          name: cookieName,
          value:
            typeof nextValue === 'string'
              ? nextValue
              : JSON.stringify(nextValue),
          path: config?.path || '/',
          expirationInDays: config?.expirationInDays || DEFAULT_EXPIRATION_DAYS,

          // for tests we want the cookie available on the test environment instead of the real shop domain
          // find a better way to do this
          ...(process.env.NODE_ENV === 'test'
            ? {}
            : {domain: config?.domain ?? defaultCookieDomain}),
        });
      }

      const stateSlice = {[cookieName]: nextValue};
      useTransformedValueStore.getState().update(stateSlice);
    },
    [defaultCookieDomain, useTransformedValueStore],
  );

  return (
    <CookieContext.Provider
      value={{
        useRawValueStore,
        useTransformedValueStore,
        updateCookie,
      }}
    >
      {children}
    </CookieContext.Provider>
  );
}
